import type { Nhom } from '@qlk/shared';
import { CACH_XU_LY_KENH_LO_LABEL, VAI_TRO_CHIA_LABEL, ngayDayVn } from '@qlk/shared';
import { useState } from 'react';

import { coQuyen, useToi } from '../../../lib/auth.js';
import {
  EmptyState,
  KhoiMuc,
  MenuThaoTac,
  Nut,
  OChon,
  StatStrip,
  StatusBadge,
  ToolbarBang,
  dinhDangTien,
  type MucThaoTac,
} from '../../../ui/index.js';
import { useLuuTruNhom, useNhom } from '../api.js';
import { HopKenhCuaNhom } from './HopKenhCuaNhom.js';
import { HopNhom } from './HopNhom.js';
import { HopTyLe } from './HopTyLe.js';

/** Sáu kỳ gần nhất, mới nhất trước. */
function kyGanDay(soKy = 6): string[] {
  const nay = new Date();
  return Array.from({ length: soKy }, (_, i) => {
    const d = new Date(Date.UTC(nay.getUTCFullYear(), nay.getUTCMonth() - i, 1));
    return `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

function nhanKy(ky: string): string {
  const [nam, thang] = ky.split('-');
  return `Tháng ${thang ?? '?'}/${nam ?? '?'}`;
}

function KhoiNhom({
  nhom,
  coSua,
  onSua,
  onKenh,
  onTruongNhom,
  onLuuTru,
  dangLuuTru,
}: {
  nhom: Nhom;
  coSua: boolean;
  onSua: () => void;
  onKenh: () => void;
  onTruongNhom: () => void;
  onLuuTru: () => void;
  dangLuuTru: boolean;
}) {
  const muc: MucThaoTac[] = [
    { nhan: 'Sửa nhóm', onChon: onSua },
    { nhan: `Kênh của nhóm (${String(nhom.soKenh)})`, onChon: onKenh },
    {
      nhan: nhom.archivedAt ? 'Khôi phục nhóm' : 'Lưu trữ nhóm',
      tat: dangLuuTru,
      onChon: onLuuTru,
    },
  ];

  return (
    <KhoiMuc
      tieuDe={nhom.name}
      {...(nhom.note ? { moTa: nhom.note } : {})}
      phai={
        <div className="flex items-center gap-1.5">
          {/* Nhóm đã lưu trữ: nói ra ngay cạnh tên, không chỉ làm mờ cả khối —
              mờ đọc ra là "đang tải" nhiều hơn là "đã lưu trữ". */}
          {nhom.archivedAt && <StatusBadge tone="neutral">Đã lưu trữ</StatusBadge>}
          {coSua && (
            <>
              <Nut kieu="phu" co="nho" onClick={onTruongNhom}>
                + Trưởng nhóm
              </Nut>
              <MenuThaoTac muc={muc} aria-label={`Thao tác với nhóm ${nhom.name}`} />
            </>
          )}
        </div>
      }
    >
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <dt className="text-phu font-medium text-chu-phu">Trưởng nhóm</dt>
          <dd className="mt-1 flex flex-wrap gap-1.5">
            {nhom.truongNhom.length === 0 ? (
              <span className="text-co-ban text-chu-phu">Chưa đặt</span>
            ) : (
              nhom.truongNhom.map((t) => (
                <StatusBadge key={t.id} tone="info" cham={false}>
                  <span title={`Hiệu lực từ ${ngayDayVn(t.hieuLucTu)}`}>
                    {t.tenNguoi}
                    {t.hinhThuc !== 'co_dinh' && (
                      <span className="so-lieu ml-1">
                        · {t.pct.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%
                      </span>
                    )}
                    {t.hinhThuc !== 'pct' && (
                      <span className="so-lieu ml-1">· {dinhDangTien(t.coDinhUsd)}/th</span>
                    )}
                  </span>
                </StatusBadge>
              ))
            )}
          </dd>
        </div>

        <div>
          <dt className="text-phu font-medium text-chu-phu">Chính sách khi kênh lỗ</dt>
          <dd className="mt-1 text-co-ban text-chu">
            {CACH_XU_LY_KENH_LO_LABEL[nhom.chinhSachKenhLo]}
          </dd>
          <dd className="text-phu text-chu-phu">
            {nhom.quanLyNhapChiPhi ? 'Quản lý kênh được nhập chi phí' : 'Chỉ quản trị nhập chi phí'}
          </dd>
        </div>

        <div>
          <dt className="text-phu font-medium text-chu-phu">
            Thành viên ({nhom.thanhVien.length})
          </dt>
          <dd className="mt-1 flex flex-wrap gap-1.5">
            {nhom.thanhVien.length === 0 ? (
              <span className="text-co-ban text-chu-phu">Chưa có ai</span>
            ) : (
              nhom.thanhVien.map((tv) => (
                /* Trước đây dùng `bg-trung-tinh-nen` / `text-trung-tinh-chu` —
                   hai lớp **không tồn tại** (token là `neutral-*`), nên chip
                   thành viên bấy lâu nay không có nền. Dùng `StatusBadge` cho
                   khỏi gõ tên token bằng tay lần nữa. */
                <StatusBadge key={tv.userId} tone="neutral" cham={false}>
                  <span title={tv.vaiTro.map((v) => VAI_TRO_CHIA_LABEL[v]).join(', ')}>
                    {tv.tenNguoi} · {tv.soKenh} kênh
                  </span>
                </StatusBadge>
              ))
            )}
          </dd>
        </div>
      </dl>
    </KhoiMuc>
  );
}

/**
 * Tab "Nhóm" của trang Nhân sự & nhóm: nhóm kênh, trưởng nhóm hưởng phần trăm
 * trên từng kênh, và chính sách chia khi kênh lỗ.
 *
 * Kỳ **của riêng tab này** (không phải ô kỳ chung ở thanh trên): tỷ lệ trưởng
 * nhóm có hiệu lực theo khoảng ngày, nên "nhóm tháng nào" là một bộ lọc của
 * bảng, trong khi phần Thành viên và Hồ sơ thuế cùng trang không có kỳ nào.
 */
export function TabNhom() {
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'team', 'admin');

  const [period, setPeriod] = useState(kyGanDay()[0] ?? '');
  const [hopNhom, setHopNhom] = useState<{ nhom?: Nhom } | null>(null);
  const [hopKenh, setHopKenh] = useState<Nhom | null>(null);
  const [hopTruongNhom, setHopTruongNhom] = useState<Nhom | null>(null);

  const nhom = useNhom(period);
  const luuTru = useLuuTruNhom();

  const dang = nhom.data ?? [];
  const soKenh = dang.reduce((s, n) => s + n.soKenh, 0);
  const soNguoi = new Set(dang.flatMap((n) => n.thanhVien.map((t) => t.userId))).size;

  return (
    <div className="flex flex-col gap-4">
      <StatStrip
        aria-label={`Chỉ số nhóm ${nhanKy(period).toLowerCase()}`}
        o={[
          { nhan: 'Nhóm', giaTri: dang.length },
          { nhan: 'Kênh đã gán nhóm', giaTri: soKenh },
          { nhan: 'Người trong đội ngũ', giaTri: soNguoi },
        ]}
      />

      <div>
        <ToolbarBang
          phai={
            <OChon
              label="Kỳ của tỷ lệ"
              value={period}
              onChange={setPeriod}
              options={kyGanDay().map((k) => ({ value: k, label: nhanKy(k) }))}
            />
          }
        />

        {nhom.error ? (
          <div className="rounded-the border border-vien bg-be-mat">
            <EmptyState
              loi
              title="Không tải được danh sách nhóm"
              description={nhom.error.message}
              action={
                <Nut
                  kieu="phu"
                  onClick={() => {
                    void nhom.refetch();
                  }}
                >
                  Thử lại
                </Nut>
              }
            />
          </div>
        ) : dang.length === 0 && !nhom.isPending ? (
          <div className="rounded-the border border-vien bg-be-mat">
            <EmptyState
              title="Chưa có nhóm nào"
              description="Tạo nhóm rồi gán kênh để trưởng nhóm hưởng phần trăm trên từng kênh trong nhóm."
              action={
                coSua ? (
                  <Nut
                    kieu="chinh"
                    onClick={() => {
                      setHopNhom({});
                    }}
                  >
                    + Tạo nhóm
                  </Nut>
                ) : undefined
              }
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {dang.map((n) => (
              <KhoiNhom
                key={n.id}
                nhom={n}
                coSua={coSua}
                dangLuuTru={luuTru.isPending}
                onSua={() => {
                  setHopNhom({ nhom: n });
                }}
                onKenh={() => {
                  setHopKenh(n);
                }}
                onTruongNhom={() => {
                  setHopTruongNhom(n);
                }}
                onLuuTru={() => {
                  luuTru.mutate({ id: n.id, luuTru: n.archivedAt === null });
                }}
              />
            ))}
          </div>
        )}
      </div>

      {hopNhom && (
        <HopNhom
          {...(hopNhom.nhom ? { nhom: hopNhom.nhom } : {})}
          onDong={() => {
            setHopNhom(null);
          }}
        />
      )}

      {hopKenh && (
        <HopKenhCuaNhom
          nhom={hopKenh}
          dangCo={hopKenh.channelIds}
          onDong={() => {
            setHopKenh(null);
          }}
        />
      )}

      {hopTruongNhom && (
        <HopTyLe
          teamId={hopTruongNhom.id}
          onDong={() => {
            setHopTruongNhom(null);
          }}
        />
      )}
    </div>
  );
}
